import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  Check,
  Bell,
  HandHelping,
  Loader2,
  ShieldCheck,
  Wallet,
  Send,
  ChevronRight,
  AlertCircle,
  CreditCard,
  Lock,
} from "lucide-react";
import api, { BACKEND_ORIGIN } from "@/lib/api";
import { money, initials, statusMeta, errMsg } from "@/lib/format";
import { colorForIndex, withAlpha } from "@/lib/colors";
import { sound } from "@/lib/sound";
import { celebrate } from "@/lib/confetti";
import AnimatedMoney from "@/components/AnimatedMoney";
import OnboardingModal from "@/components/OnboardingModal";
import { useAuth } from "@/context/AuthContext";
import useSocket from "@/hooks/useSocket";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import StatusPill from "@/components/StatusPill";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";

export default function BillRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bill, setBill] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paying, setPaying] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [rq, setRq] = useState(null); // release quote (host only, when funded)
  const [releasing, setReleasing] = useState(false);
  const [connect, setConnect] = useState(null); // host payout-setup status
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [feeQuote, setFeeQuote] = useState(null); // fee breakdown for my share
  const [showFees, setShowFees] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/bills/${id}`);
      setBill(r.data);
      const ff = ["fully_funded_confirmed", "settled"].includes(r.data.status);
      const iAmPayer = r.data.designated_payer_id === user?.id;
      if (ff && iAmPayer) {
        try {
          const q = await api.get(`/bills/${id}/release-quote`);
          setRq(q.data);
        } catch (err) {
          console.debug("release-quote unavailable (non-fatal)", err);
        }
      } else {
        setRq(null);
      }
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
    }
  }, [id, user]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 2500); // near-realtime fallback when WS can't upgrade
    return () => {
      clearInterval(poll);
      clearInterval(pollRef.current);
    };
  }, [load]);

  useSocket(user?.id, (ev) => {
    if (ev.bill_id === id) load();
  });

  const me = useMemo(
    () => bill?.participants?.find((p) => p.user_id === user?.id),
    [bill, user]
  );
  // Stable per-participant color + quick lookup (creator = first color).
  const colorMap = useMemo(() => {
    const m = {};
    (bill?.participants || []).forEach((p, i) => {
      m[p.user_id] = colorForIndex(i);
    });
    return m;
  }, [bill]);
  const participantsById = useMemo(() => {
    const m = {};
    (bill?.participants || []).forEach((p) => {
      m[p.user_id] = p;
    });
    return m;
  }, [bill]);
  const isCreator = bill?.creator_id === user?.id;

  // Host payout readiness — fetched once when the creator opens their bill so
  // we can nudge them to set up payouts up front (not at settlement time).
  useEffect(() => {
    if (bill && isCreator && connect === null) {
      api
        .get("/billing/connect/status")
        .then((r) => setConnect(r.data))
        .catch(() => setConnect({})); // sentinel: don't refetch every poll on error
    }
  }, [bill, isCreator, connect]);

  const items = bill?.items || [];
  const hasItems = items.length > 0;
  const total = Number(bill?.total_amount || 0);
  const confirmed = Number(bill?.funded_confirmed || 0);
  const pct = total > 0 ? Math.min(100, Math.round((confirmed / total) * 100)) : 0;
  const myShare = Number(me?.share_amount || 0);
  const iPaid =
    me?.contribution_status === "confirmed" ||
    ["funded", "paid"].includes(me?.status);

  // Celebrate meaningful money milestones — but never on the very first render
  // (a bill that's already paid when you open it shouldn't fire confetti).
  const prevPaidRef = useRef(null);
  const prevSettledRef = useRef(null);
  useEffect(() => {
    if (!bill) return;
    const settled = bill.status === "settled";
    if (prevPaidRef.current === null) {
      prevPaidRef.current = iPaid;
      prevSettledRef.current = settled;
      return;
    }
    if (!prevPaidRef.current && iPaid) {
      sound.play("success");
      celebrate({ y: 0.4 });
    }
    if (!prevSettledRef.current && settled) {
      sound.play("cash");
      celebrate({ y: 0.35, count: 160 });
    }
    prevPaidRef.current = iPaid;
    prevSettledRef.current = settled;
  }, [iPaid, bill]);

  // Fetch the exact fee breakdown for my share so we show what the guest will
  // actually be charged before they tap Pay (no surprise at Stripe).
  useEffect(() => {
    if (!bill || !me || myShare <= 0) return;
    let cancel = false;
    api
      .get(`/funding/options/${id}`)
      .then((r) => !cancel && setFeeQuote(r.data))
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [bill, myShare]);

  const inviteLink = bill?.invite_code
    ? `${window.location.origin}/j/${bill.invite_code}`
    : "";

  const copyInvite = async () => {
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const toggleClaim = async (itemId) => {
    if (bill.funding_locked) return toast.error("Bill is locked");
    const current = new Set(
      items.filter((it) => (it.assigned_to || []).includes(user.id)).map((it) => it.id)
    );
    const adding = !current.has(itemId);
    if (current.has(itemId)) current.delete(itemId);
    else current.add(itemId);
    sound.play(adding ? "pop" : "tap");
    setClaimingId(itemId);
    try {
      const r = await api.post(`/bills/${id}/items/claim`, {
        item_ids: Array.from(current),
      });
      setBill((b) => ({ ...b, ...r.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update claim");
    } finally {
      setClaimingId(null);
    }
  };

  const pollStatus = (sessionId) => {
    clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      if (attempts > 40) {
        clearInterval(pollRef.current); // give up after ~2 min
        return;
      }
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "completed") {
          clearInterval(pollRef.current);
          toast.success("Payment confirmed 🎉");
          load();
        }
      } catch (err) {
        console.debug("payment status poll failed; will retry", err);
      }
    }, 3000);
  };

  const pay = () => {
    sound.play("tap");
    // Open the checkout tab SYNCHRONOUSLY inside the click gesture — mobile
    // browsers block window.open() that runs after an await. If the popup is
    // blocked anyway (e.g. an in-app browser from a texted link), we fall back
    // to a same-tab redirect, which is never blocked.
    const win = window.open("about:blank", "_blank");
    setPaying(true);
    (async () => {
      try {
        const r = await api.post("/payments/create", {
          bill_id: id,
          method: "card",
          origin_url: BACKEND_ORIGIN,
        });
        const url = r.data?.checkout_url;
        if (!url) {
          if (win) win.close();
          toast.error("Could not start payment");
          setPaying(false);
          return;
        }
        if (win && !win.closed) {
          win.location.href = url;
          pollStatus(r.data.session_id);
          toast.info("Complete payment in the new tab — this page updates automatically");
          load();
          setPaying(false);
        } else {
          // popup blocked → same-tab redirect (works everywhere)
          toast.info("Redirecting to secure checkout…");
          window.location.href = url;
        }
      } catch (err) {
        if (win) win.close();
        toast.error(errMsg(err, "Could not start payment"));
        setPaying(false);
      }
    })();
  };

  const cover = async () => {
    try {
      await api.post(`/bills/${id}/cover`);
      toast.success("You're covering this bill");
      load();
    } catch (err) {
      toast.error(
        err.response?.data?.detail?.message ||
          err.response?.data?.detail ||
          "Could not activate cover"
      );
    }
  };

  const remind = async () => {
    try {
      await api.post(`/bills/${id}/remind`);
      toast.success("Reminder sent to pending people");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reminder");
    }
  };

  const release = async () => {
    setReleasing(true);
    try {
      const r = await api.post(`/bills/${id}/release`);
      sound.play("cash");
      if (r.data.status === "queued")
        toast.info("Release is processing — we'll update it automatically");
      else
        toast.success(
          r.data.simulated
            ? "Funds released to you (test mode)"
            : "Funds released to your account"
        );
      load();
    } catch (err) {
      toast.error(errMsg(err, "Could not release funds"));
    } finally {
      setReleasing(false);
    }
  };

  const confirmMerchantPaid = async () => {
    try {
      await api.post(`/bills/${id}/confirm-merchant-paid`);
      sound.play("cash");
      celebrate({ y: 0.35, count: 160 });
      toast.success("Nice — bill settled. Merchant paid!");
      load();
    } catch (err) {
      sound.play("error");
      toast.error(errMsg(err, "Could not confirm"));
    }
  };

  if (notFound)
    return (
      <div className="text-center py-20">
        <p className="text-white font-bold text-lg">Bill not found</p>
        <button onClick={() => navigate("/")} className="text-[#c084fc] mt-2">
          Back home
        </button>
      </div>
    );

  if (!bill)
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 rounded-3xl bg-white/5" />
        <Skeleton className="h-64 rounded-3xl bg-white/5" />
      </div>
    );

  const fullyFunded = ["fully_funded_confirmed", "settled"].includes(bill.status);

  return (
    <div className="space-y-6 pb-24">
      <OnboardingModal
        open={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        onComplete={(s) => {
          if (s) setConnect(s);
          load();
        }}
      />
      <button
        onClick={() => navigate("/")}
        data-testid="back-btn"
        className="flex items-center gap-2 text-muted-foreground hover:text-white text-sm"
      >
        <ArrowLeft size={16} /> All bills
      </button>

      {/* Header */}
      <div className="hp-glass rounded-3xl p-6 hp-fade-in">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              {bill.category_emoji && <span className="text-xl">{bill.category_emoji}</span>}
              <h1 className="text-2xl font-extrabold text-white">{bill.name}</h1>
            </div>
            {bill.merchant && (
              <p className="text-sm text-muted-foreground mt-0.5">{bill.merchant}</p>
            )}
          </div>
          <StatusPill tone={fullyFunded ? "success" : "info"}>
            {fullyFunded ? "Fully funded" : "Collecting"}
          </StatusPill>
        </div>

        <div className="mt-5">
          <div className="flex items-end justify-between mb-2">
            <AnimatedMoney value={total} className="text-4xl font-extrabold text-white" />
            <span className="text-sm text-muted-foreground">{pct}% funded</span>
          </div>
          <Progress value={pct} className="h-2.5 bg-white/5 hp-progress-glow" />
          <p className="text-xs text-muted-foreground mt-1.5">
            {money(confirmed)} of {money(total)} collected
          </p>
        </div>

        {fullyFunded && (
          <div className="mt-4 flex items-center gap-2 text-sm text-[#34d399] bg-[#34d399]/10 border border-[#34d399]/25 rounded-xl px-3 py-2.5">
            <ShieldCheck size={18} />
            Fully funded — ready to pay the merchant.
          </div>
        )}
      </div>

      {/* Host: set up payouts BEFORE sharing so guests never wait */}
      {isCreator && connect && !connect.payouts_enabled && bill.status !== "settled" && (
        <div
          data-testid="setup-payouts-gate"
          className="hp-glass-strong rounded-3xl p-6 border border-[#a855f7]/40"
        >
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl grid place-items-center bg-[#7c3aed]/25 text-[#c084fc] shrink-0">
              <Wallet size={24} />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-extrabold text-white">Open your bill</p>
              <p className="text-[13px] text-muted-foreground">One-time setup · about 2 minutes</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3 leading-snug">
            Set up payouts <span className="text-white font-semibold">before you share</span> so
            the second your table scans the code, they can pay instantly — nobody waits on you.
          </p>
          <button
            data-testid="setup-payouts-banner"
            onClick={() => {
              sound.play("tap");
              setOnboardOpen(true);
            }}
            className="hp-cta inline-flex items-center gap-2 mt-4 px-6 h-12 rounded-xl font-bold text-white border-0"
          >
            <Wallet size={18} /> Set up payouts to open bill
          </button>
        </div>
      )}

      {isCreator && connect && connect.payouts_enabled && bill.status !== "settled" && (
        connect.instant_eligible ? (
          <div
            data-testid="payouts-ready-chip"
            className="flex items-center gap-2 text-sm text-[#34d399] bg-[#34d399]/10 border border-[#34d399]/25 rounded-2xl px-4 py-2.5"
          >
            <ShieldCheck size={16} /> Payouts ready — you can pay the merchant instantly
            when everyone&apos;s paid.
          </div>
        ) : (
          <div
            data-testid="payouts-slow-warning"
            className="rounded-2xl px-4 py-3 border border-amber-400/30 bg-amber-400/[0.07]"
          >
            <div className="flex items-start gap-2.5">
              <AlertCircle size={17} className="text-amber-300 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-200">
                  Add a debit card — settle in less than 30 minutes
                </p>
                <p className="text-[13px] text-amber-100/70 mt-0.5 leading-snug">
                  With a debit card you can pay yourself and the merchant in{" "}
                  <span className="font-semibold text-amber-200">less than 30 minutes</span>,
                  right at the table. Takes about a minute to add.
                </p>
                <Link
                  to="/payouts"
                  data-testid="goto-payouts-instant-btn"
                  className="hp-cta inline-flex items-center gap-2 mt-2.5 px-4 py-2 rounded-xl text-sm font-bold text-white border-0"
                >
                  <CreditCard size={15} /> Add a debit card
                </Link>
              </div>
            </div>
          </div>
        )
      )}

      {/* Host: collect & pay the merchant */}
      {rq && (
        <div className="hp-glass rounded-3xl p-5 border border-[#a855f7]/25">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={18} className="text-[#c084fc]" />
            <p className="text-sm font-bold text-white">Collect &amp; pay the merchant</p>
          </div>

          {!rq.designated_payer?.payouts_enabled &&
          rq.release_status !== "paid_merchant" ? (
            <div>
              <p className="text-sm text-muted-foreground">
                Set up payouts to move the{" "}
                {money(rq.expected_funded_total ?? rq.funded_confirmed)} everyone paid
                into your account.
              </p>
              <Link
                to="/payouts"
                data-testid="goto-payouts-btn"
                className="hp-cta inline-flex items-center gap-2 mt-3 px-5 py-2.5 rounded-xl font-bold text-white border-0"
              >
                <Wallet size={16} /> Set up payouts
              </Link>
            </div>
          ) : rq.release_status === "pending" ? (
            <div>
              <p className="text-sm text-muted-foreground">
                Everyone&apos;s paid. Release the funds to your account, then pay the
                merchant.
              </p>
              <button
                data-testid="release-funds-btn"
                onClick={release}
                disabled={releasing}
                className="hp-cta w-full mt-3 h-12 rounded-xl font-bold text-white border-0"
              >
                {releasing
                  ? "Releasing…"
                  : `Release ${money(rq.expected_funded_total ?? rq.funded_confirmed)} to me`}
              </button>
            </div>
          ) : rq.release_status === "queued" ? (
            <p className="text-sm text-amber-300 flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Release is
              processing — no action needed.
            </p>
          ) : rq.release_status === "released" ? (
            <div>
              <div className="flex items-center gap-2 text-sm text-[#34d399] mb-3">
                <ShieldCheck size={18} /> Funds released to your account.
              </div>
              <button
                data-testid="confirm-merchant-paid-btn"
                onClick={confirmMerchantPaid}
                className="w-full h-12 rounded-xl font-bold text-white flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(135deg,#34d399,#059669)" }}
              >
                <Send size={16} /> I&apos;ve paid the merchant
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-[#34d399]">
              <ShieldCheck size={18} /> Settled — merchant paid. 🎉
            </div>
          )}
        </div>
      )}

      {/* Invite — locked until the host has set up payouts, so guests never wait */}
      {isCreator && connect && !connect.payouts_enabled && bill.status !== "settled" ? (
        <div
          data-testid="invite-locked"
          className="hp-glass rounded-3xl p-6 text-center border border-dashed border-white/15"
        >
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-white/5 text-muted-foreground mx-auto mb-2">
            <Lock size={20} />
          </div>
          <p className="text-sm font-bold text-white">Your invite QR is locked</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-[16rem] mx-auto">
            Finish payout setup above to open this bill — then share the QR and your table
            pays instantly.
          </p>
        </div>
      ) : (
      <div className="hp-glass rounded-3xl p-5">
        <p className="text-sm font-bold text-white mb-1">Invite your crew</p>
        <p className="text-xs text-muted-foreground mb-4">
          Everyone opens the same bill, claims their items, and pays their share. No
          signup needed.
        </p>

        {inviteLink && (
          <div className="flex flex-col items-center mb-4">
            <div className="bg-white p-3 rounded-2xl shadow-lg" data-testid="bill-qr">
              <QRCodeCanvas
                value={inviteLink}
                size={188}
                bgColor="#ffffff"
                fgColor="#0b0619"
                level="M"
                includeMargin={false}
              />
            </div>
            <p className="text-[13px] font-semibold text-white mt-3">Scan to join &amp; pay</p>
            <p className="text-xs text-muted-foreground">Point any phone camera — no app needed</p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-muted-foreground truncate">
            {inviteLink}
          </div>
          <button
            data-testid="copy-invite-btn"
            onClick={copyInvite}
            className="h-11 w-11 grid place-items-center rounded-xl bg-[#7c3aed]/25 text-[#c084fc] hover:bg-[#7c3aed]/40 transition-all"
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Code: <span className="font-mono text-white">{bill.invite_code}</span>
        </p>
      </div>
      )}

      {/* Items */}
      {hasItems && (
        <div className="hp-glass rounded-3xl p-5">
          <p className="text-sm font-bold text-white mb-1">Claim your items</p>
          <p className="text-xs text-muted-foreground mb-3">
            Tap the items you had. Shared items split evenly between everyone who
            claims them; unclaimed items split evenly across everyone.
          </p>
          <div className="space-y-2">
            {items.map((it) => {
              const mine = (it.assigned_to || []).includes(user.id);
              const claimers = (it.assigned_to || []).length;
              const approxEach =
                claimers > 1 && Math.round(it.price * 100) % claimers !== 0;
              return (
                <button
                  key={it.id}
                  data-testid={`item-claim-${it.id}`}
                  onClick={() => toggleClaim(it.id)}
                  disabled={claimingId === it.id || bill.funding_locked}
                  style={
                    mine
                      ? {
                          backgroundColor: withAlpha(colorMap[user.id] || "#a855f7", 0.18),
                          borderColor: withAlpha(colorMap[user.id] || "#a855f7", 0.6),
                        }
                      : undefined
                  }
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border text-left transition-all ${
                    mine ? "" : "bg-white/5 border-white/10 hover:border-white/20"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="h-6 w-6 rounded-lg grid place-items-center border shrink-0"
                      style={
                        mine
                          ? {
                              backgroundColor: colorMap[user.id],
                              borderColor: colorMap[user.id],
                              color: "#0b0619",
                            }
                          : { borderColor: "rgba(255,255,255,0.25)", color: "transparent" }
                      }
                    >
                      {claimingId === it.id ? (
                        <Loader2
                          size={14}
                          className="animate-spin"
                          style={{ color: mine ? "#0b0619" : "#fff" }}
                        />
                      ) : (
                        <Check size={14} />
                      )}
                    </div>
                    <span className="text-white font-medium truncate">{it.name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-right">
                    {claimers > 0 && (
                      <div className="flex -space-x-1.5" data-testid={`item-claimers-${it.id}`}>
                        {(it.assigned_to || []).slice(0, 4).map((uid) => (
                          <div
                            key={uid}
                            title={participantsById[uid]?.name}
                            className="h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold ring-2 ring-[#0b0619]"
                            style={{ backgroundColor: colorMap[uid] || "#a855f7", color: "#0b0619" }}
                          >
                            {initials(participantsById[uid]?.name)}
                          </div>
                        ))}
                        {claimers > 4 && (
                          <div className="h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold bg-white/15 text-white ring-2 ring-[#0b0619]">
                            +{claimers - 4}
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      {claimers > 1 ? (
                        <>
                          <div className="text-white font-semibold">
                            {approxEach ? "≈" : ""}
                            {money(it.price / claimers)}{" "}
                            <span className="text-xs font-normal text-muted-foreground">
                              each
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            split {claimers} ways · {money(it.price)}
                          </div>
                        </>
                      ) : (
                        <span className="text-white font-semibold">
                          {money(it.price)}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {Number(bill.unclaimed_amount || 0) > 0 && (
            <p className="text-xs text-amber-300 mt-3">
              {money(bill.unclaimed_amount)} still unclaimed — split evenly across
              everyone.
            </p>
          )}
        </div>
      )}

      {/* Participants */}
      <div className="hp-glass rounded-3xl p-5">
        <p className="text-sm font-bold text-white mb-3">
          People ({bill.participants?.length || 0})
        </p>
        <div className="space-y-2">
          {(bill.participants || []).map((p) => {
            const meta = statusMeta(p);
            const isMe = p.user_id === user.id;
            return (
              <div
                key={p.user_id}
                className="flex items-center justify-between gap-3 py-2"
                data-testid={`participant-${p.user_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="h-9 w-9 rounded-full grid place-items-center text-xs font-bold"
                    style={{
                      backgroundColor: colorMap[p.user_id] || "#7c3aed",
                      color: "#0b0619",
                    }}
                  >
                    {initials(p.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">
                      {p.name} {isMe && <span className="text-muted-foreground">(you)</span>}
                    </p>
                    {p.share_amount != null && (
                      <p className="text-xs text-muted-foreground">
                        {money(p.share_amount)}
                      </p>
                    )}
                  </div>
                </div>
                <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
              </div>
            );
          })}
        </div>

        {isCreator && (
          <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
            <button
              data-testid="remind-btn"
              onClick={remind}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 text-white font-semibold text-sm hover:bg-white/10"
            >
              <Bell size={16} /> Remind
            </button>
            <button
              data-testid="cover-btn"
              onClick={cover}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 text-white font-semibold text-sm hover:bg-white/10"
            >
              <HandHelping size={16} /> Cover the rest
            </button>
          </div>
        )}
      </div>

      {/* Spacer so content clears the floating pay bar + tab bar */}
      {me && myShare > 0 && <div className="h-24" aria-hidden="true" />}

      {/* Sticky pay bar — sits above the bottom tab bar */}
      {me && myShare > 0 && (
        <div className="fixed bottom-24 inset-x-0 z-40 px-5 flex justify-center pointer-events-none">
          <div className="hp-glass-strong w-full max-w-[420px] rounded-2xl p-4 shadow-2xl pointer-events-auto bg-[#0e0a16]/97">
            {!iPaid && showFees && feeQuote && (
              <div
                data-testid="fee-breakdown"
                className="mb-3 pb-3 border-b border-white/10 space-y-1.5 text-[13px]"
              >
                <div className="flex justify-between text-muted-foreground">
                  <span>Your share</span>
                  <span className="text-white">{money(feeQuote.base_amount)}</span>
                </div>
                {feeQuote.platform_fee_card > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>HonestPay fee</span>
                    <span className="text-white">{money(feeQuote.platform_fee_card)}</span>
                  </div>
                )}
                <div className="flex justify-between text-muted-foreground">
                  <span>Card processing</span>
                  <span className="text-white">{money(feeQuote.processor_fee_card)}</span>
                </div>
                <div className="flex justify-between pt-1.5 border-t border-white/5 font-bold text-white">
                  <span>Total</span>
                  <span>{money(feeQuote.card_total)}</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{iPaid ? "Your share" : "You pay"}</p>
                <span className="text-xl font-extrabold text-white">
                  {money(iPaid ? myShare : feeQuote?.card_total ?? myShare)}
                </span>
                {!iPaid && feeQuote && feeQuote.card_total - feeQuote.base_amount > 0 && (
                  <button
                    data-testid="fee-breakdown-toggle"
                    onClick={() => setShowFees((v) => !v)}
                    className="block text-[11px] text-muted-foreground underline decoration-dotted underline-offset-2 mt-0.5"
                  >
                    {money(feeQuote.base_amount)} share + {money(feeQuote.card_total - feeQuote.base_amount)} fees
                    {showFees ? " ▲" : " ▾"}
                  </button>
                )}
              </div>
              {iPaid ? (
                <StatusPill tone="success" testId="you-paid-pill">
                  <Check size={14} className="mr-1" /> Paid
                </StatusPill>
              ) : (
                <button
                  data-testid="pay-btn"
                  onClick={pay}
                  disabled={paying}
                  className="hp-cta h-11 px-7 rounded-xl font-bold text-white border-0 shrink-0"
                >
                  {paying ? "Starting…" : `Pay ${money(feeQuote?.card_total ?? myShare)}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
